import { useEffect, useState } from 'react';
import { Box, Button, Flex, Grid, GridItem, HStack, VStack, useDisclosure, useColorModeValue } from '@chakra-ui/react';
import ABTestModal from './add_ab_test_modal';
import ABTestTable from './components/ab_test_table';
import { ABTestHeader } from './components/ab_test_header';
import config from '../config';
import axios from 'axios';
import { RemoteConfigValue } from '@repo/alictus-common/types';
import globalStore from '../store';
import { ViewFilter } from './ab_test_view_filter';
import { PlayerSegment } from '@repo/alictus-common/types/segment';
import { ABTestStreamView } from '@repo/alictus-common/types/ab_test';
import { DeleteABTestModal } from './delete_ab_test_modal';

function ABTestView({ userToken, selectedGameId }: { userToken: string; selectedGameId: string | null }) {
	let store = globalStore();
	const isAdminOrProduct = store.user!.roles.includes('ADMIN') || store.user!.roles.includes('PRODUCT') || false;
	const [loading, setLoading] = useState(true);
	const [isAdmin, setIsAdmin] = useState(false);
	const deleteABTestDisclosure = useDisclosure();
	const { isOpen, onOpen, onClose } = useDisclosure();
	const [viewFilter, setViewFilter] = useState(new ViewFilter());
	const [deleteABTestData, setDeleteABTestData] = useState<any>(null);
	const [remoteAbTests, setRemoteAbTests] = useState<ABTestStreamView[]>([]);
	const [segmentationData, setSegmentationData] = useState<PlayerSegment[]>([]);
	const [isSegmentationDataFetched, setIsSegmentationDataFetched] = useState(false);
	const [isRemoteConfigDataFetched, setIsRemoteConfigDataFetched] = useState(false);
	const [playerCounts, setPlayerCounts] = useState<Map<string, number>>(new Map());
	const [dailyUserCounts, setDailyUserCounts] = useState<Map<string, number>>(new Map());
	const [refreshingCounts, setRefreshingCounts] = useState(false);

	const borderColor = useColorModeValue('gray.300', 'gray.600');

	// Function to fetch cached data (page load - FAST)
	const fetchCachedPlayerCounts = async () => {
		try {
			let bearerToken = 'Bearer ' + store.JWTToken;
			let url = config.CLOUDFLARE_LOCAL_ENDPOINT + '/game/' + store.gameId + '/ab_test/player_counts/cached';
			const response = await axios.get(url, { headers: { Authorization: bearerToken } });
			const counts = response.data as { abTestUid: string; playerCount: number; dailyUserCount: number }[];

			const totalCountMap = new Map<string, number>();
			const dailyCountMap = new Map<string, number>();

			counts.forEach((count) => {
				totalCountMap.set(count.abTestUid, count.playerCount);
				dailyCountMap.set(count.abTestUid, count.dailyUserCount);
			});

			setPlayerCounts(totalCountMap);
			setDailyUserCounts(dailyCountMap);
		} catch (error) {
			console.error('Error fetching cached player counts:', error);
		}
	};

	// Function to refresh all data (button click - queries analytics table)
	const refreshRunningPlayerCounts = async () => {
		setRefreshingCounts(true);
		try {
			let bearerToken = 'Bearer ' + store.JWTToken;
			let url = config.CLOUDFLARE_LOCAL_ENDPOINT + '/game/' + store.gameId + '/ab_test/player_counts/running';
			const response = await axios.get(url, { headers: { Authorization: bearerToken } });
			const counts = response.data as { abTestUid: string; playerCount: number; dailyPlayerCount: number }[];

			// Get cached counts first
			let cachedCounts: { abTestUid: string; playerCount: number; dailyUserCount: number }[] = [];
			try {
				let cachedUrl = config.CLOUDFLARE_LOCAL_ENDPOINT + '/game/' + store.gameId + '/ab_test/player_counts/cached';
				const cachedResponse = await axios.get(cachedUrl, { headers: { Authorization: bearerToken } });
				cachedCounts = cachedResponse.data;
			} catch (err) {
				console.error('Error fetching cached counts during refresh:', err);
			}

			// Merge refreshed counts with cached counts
			const totalCountMap = new Map<string, number>();
			const dailyCountMap = new Map<string, number>();

			// Add all cached counts first
			cachedCounts.forEach((count) => {
				totalCountMap.set(count.abTestUid, count.playerCount);
				dailyCountMap.set(count.abTestUid, count.dailyUserCount);
			});

			// Overwrite with refreshed counts
			counts.forEach((count) => {
				totalCountMap.set(count.abTestUid, count.playerCount);
				dailyCountMap.set(count.abTestUid, count.dailyPlayerCount);
			});

			setPlayerCounts(totalCountMap);
			setDailyUserCounts(dailyCountMap);
		} catch (error) {
			console.error('Error refreshing running player counts:', error);
		} finally {
			setRefreshingCounts(false);
		}
	};

	useEffect(() => {
		const controller = new AbortController();
		const { signal } = controller;
		setIsAdmin(store.user!.roles.includes('ADMIN'));
		const fetchData = async () => {
			try {
				while (true) {
					let bearerToken = 'Bearer ' + store.JWTToken;
					let url = config.CLOUDFLARE_LOCAL_ENDPOINT + '/game/' + store.gameId + '/ab_test/stream/0';
					const response = await axios.get(url, { headers: { Authorization: bearerToken }, signal });
					let abTestStreamViews = response.data as ABTestStreamView[];
					abTestStreamViews = abTestStreamViews.filter(
						(abTestStreamView) => store.user!.roles.includes('MOCK_CREATOR') || !abTestStreamView.abTest.isMock,
					);
					setRemoteAbTests(abTestStreamViews);
					setLoading(false);
					await new Promise((resolve) => setTimeout(resolve, 5000));
				}
			} catch (error) {
				if (axios.isCancel(error)) {
					console.log('Fetch request was canceled:', error.message);
				} else {
					console.error('Error fetching AB tests:', error);
				}
			} finally {
				setLoading(false);
			}
		};

		fetchData();

		const fetchRemoteConfigData = async () => {
			try {
				let bearerToken = 'Bearer ' + store.JWTToken;
				let url = config.CLOUDFLARE_LOCAL_ENDPOINT + '/game/' + store.gameId + '/rc_package/stream/0/all';
				const response = await axios.get(url, { headers: { Authorization: bearerToken } });
				let incomingRemoteConfigValues = response.data as RemoteConfigValue[];
				if (incomingRemoteConfigValues) {
					//Filter RC data to show values that have same name only once
					incomingRemoteConfigValues = incomingRemoteConfigValues.filter(
						(value, index, self) => self.findIndex((t) => t.key === value.key) === index,
					);

					//Filter isMock values
					incomingRemoteConfigValues = incomingRemoteConfigValues.filter(
						(value) => store.user!.roles.includes('MOCK_CREATOR') || !value.isMock,
					);

					// Sort the remote config values by key
					incomingRemoteConfigValues.sort((a, b) => (a.key > b.key ? 1 : -1));

					store.setRemoteConfigValues(incomingRemoteConfigValues);
				}
			} catch (error) {
				if (axios.isCancel(error)) {
					console.log('Fetch request was canceled:', error.message);
				} else {
					console.error('Error fetching remote config data:', error);
				}
			} finally {
				setLoading(false);
			}
		};

		const fetchSegmentationData = async () => {
			try {
				let bearerToken = 'Bearer ' + store.JWTToken;
				let url = config.CLOUDFLARE_LOCAL_ENDPOINT + '/game/' + store.gameId + '/segment';
				const response = await axios.get(url, { headers: { Authorization: bearerToken } });
				let playerSegments = response.data as PlayerSegment[];

				setSegmentationData(playerSegments);
				setViewFilter((prevFilter) => {
					const newFilter = new ViewFilter();
					Object.assign(newFilter, prevFilter);
					newFilter.setSegmentationData(playerSegments);
					return newFilter;
				});
			} catch (error) {
				console.error('Error fetching segmentation data:', error);
			} finally {
				setLoading(false);
			}
		};

		if (!isRemoteConfigDataFetched) {
			setIsRemoteConfigDataFetched(true);
			fetchRemoteConfigData();
		}
		if (!isSegmentationDataFetched) {
			fetchSegmentationData();
			setIsSegmentationDataFetched(true);
		}

		// Fetch cached player counts initially
		if (selectedGameId !== null) {
			fetchCachedPlayerCounts();
		}
		return () => controller.abort();
	}, [store, isSegmentationDataFetched, isRemoteConfigDataFetched, selectedGameId]);

	return (
		<Grid
			height={'1px'}
			width={'100%'}
			templateRows="repeat(4, 1fr)"
			templateColumns="repeat(5, 1fr)"
			gap={2}
			paddingLeft={'12px'}
			paddingRight={'10px'}
		>
			<GridItem paddingLeft={'5px'} paddingRight={'5px'} colSpan={6}>
				<Box border={'2px'} borderRadius={'5px'} borderColor={borderColor}>
					{userToken && selectedGameId && isOpen && (
						<ABTestModal
							title={'Create AB Test'}
							isOpen={isOpen}
							onClose={onClose}
							abTestToUpdate={undefined}
							isAdminOrProduct={isAdminOrProduct}
						/>
					)}
					{deleteABTestDisclosure.isOpen && deleteABTestData && isAdmin && (
						<DeleteABTestModal disclosure={deleteABTestDisclosure} abTestName={deleteABTestData.name} abTestId={deleteABTestData.id} />
					)}
					<VStack spacing={1} align="stretch">
						<HStack width={'100%'}>
							{selectedGameId !== null && (
								<ABTestHeader
									viewFilter={viewFilter}
									segmentationData={segmentationData}
									setViewFilter={setViewFilter}
									remoteABTests={remoteAbTests}
									onCreateABTest={onOpen}
									isAdminOrProduct={isAdminOrProduct}
									onRefreshPlayerCounts={refreshRunningPlayerCounts}
									refreshingCounts={refreshingCounts}
								/>
							)}
						</HStack>
						{selectedGameId !== null && (
							<ABTestTable
								globalData={store}
								remoteAbTests={remoteAbTests}
								viewFilter={viewFilter}
								setRemoteAbTests={setRemoteAbTests}
								loading={loading}
								deleteABTestDisclosure={deleteABTestDisclosure}
								setDeleteABTestData={setDeleteABTestData}
								isAdmin={isAdmin}
								isAdminOrProduct={isAdminOrProduct}
								playerCounts={playerCounts}
								dailyUserCounts={dailyUserCounts}
								refreshingCounts={refreshingCounts}
								onRefreshPlayerCounts={refreshRunningPlayerCounts}
							/>
						)}
					</VStack>
				</Box>
			</GridItem>
		</Grid>
	);
}

export default ABTestView;
